// Verträge für Kunden und Lieferanten. Beide liegen in einer Tabelle (eine Brauerei kann beides sein) und teilen
// eine Implementierung – aber jede Rolle hat ihre eigenen Verträge und Rechte, damit die Rollenmatrix „Einkauf
// sieht Lieferanten, Vertrieb sieht Kunden“ abbilden kann:
//   masterdata.customer.list | get | create | update | deactivate | reactivate | upsert | account
//   masterdata.supplier.…    dieselben acht
import { defineContract, defineEvent, type PermissionKey } from '@shared/contract'
import { EINVOICE_SYNTAXES, PAYMENT_METHODS } from '@shared/einvoice'
import { zId } from '@shared/ids'
import { listInput, listOutput } from '@shared/list'
import { TAX_CASES } from '@shared/taxCase'
import { z } from 'zod'
import {
  ADDRESS_KINDS,
  COLLECTIVE_INVOICINGS,
  EINVOICE_FORMATS,
  ORDER_CHANNELS,
  PARTNER_ROLE_LABELS,
  PARTNER_TYPES,
  type PartnerRole,
} from '../lib/labels'
import {
  zBasisPoints,
  zById,
  zCentsAmount,
  zDay,
  zDays,
  zExpectedVersion,
  zOptionalId,
  zOptionalText,
  zSaved,
  zText,
  zTimestamp,
  zToggle,
} from './common'

// ---- Datenformen ------------------------------------------------------------------------------------------

const zEmail = z.email({ error: 'Bitte eine gültige E-Mail-Adresse eingeben.' }).max(200).nullish()

const zCountry = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2}$/, { error: 'Bitte das Land als zweistelliges Kürzel angeben (DE, AT, IT …).' })

export const zAddressInput = z.strictObject({
  /** mit `id` wird die vorhandene Anschrift geändert, ohne `id` entsteht eine neue */
  id: zOptionalId,
  kind: z.enum(ADDRESS_KINDS).default('billing'),
  /** abweichender Empfänger („Festzelt Eingang Süd“) */
  name: zOptionalText(160),
  street: zOptionalText(160),
  zip: zOptionalText(12),
  city: zText(120),
  country: zCountry.default('DE'),
  isDefault: z.boolean().default(false),
  /** Anlieferhinweis: Abladestelle, Zeitfenster */
  note: zOptionalText(500),
})

export const zContactInput = z.strictObject({
  id: zOptionalId,
  name: zText(160),
  email: zEmail,
  phone: zOptionalText(60),
  /** Aufgabe: „Einkauf“, „Buchhaltung“, „Festwirt“ */
  role: zOptionalText(80),
  isPrimary: z.boolean().default(false),
})

const zWeekdays = z
  .array(z.int().min(1).max(7))
  .max(7)
  .refine((days) => new Set(days).size === days.length, { error: 'Jeder Wochentag darf nur einmal vorkommen.' })

export const zSupplierTermsInput = z.strictObject({
  minOrderValueCents: zCentsAmount.nullish(),
  freightFreeFromCents: zCentsAmount.nullish(),
  leadTimeDays: zDays.nullish(),
  /** 1 = Montag … 7 = Sonntag */
  orderWeekdays: zWeekdays.default([]),
  deliveryWeekdays: zWeekdays.default([]),
  orderChannel: z.enum(ORDER_CHANNELS).nullish(),
  orderEmail: zEmail,
})

/** SEPA-Lastschriftmandat (BG-19): Pflicht bei Zahlart Lastschrift (§5.2 Nr. 5). */
export const zSepaMandateInput = z.strictObject({
  /** Mandatsreferenz, höchstens 35 Zeichen (BT-89) */
  reference: z.string().trim().min(1, { error: 'Bitte die Mandatsreferenz angeben.' }).max(35, { error: 'Die Mandatsreferenz hat höchstens 35 Zeichen.' }),
  /** Tag der Unterschrift */
  signedAt: zDay,
  /** IBAN des Kunden (BT-91) */
  iban: z.string().trim().min(1, { error: 'Bitte die IBAN des Kunden angeben.' }).max(42),
})

export const zSepaMandate = z.object({ reference: z.string(), signedAt: z.string().nullable(), iban: z.string() })

export const zAddress = z.object({
  id: zId,
  kind: z.enum(ADDRESS_KINDS),
  name: z.string().nullable(),
  street: z.string().nullable(),
  zip: z.string().nullable(),
  city: z.string().nullable(),
  country: z.string(),
  isDefault: z.boolean(),
  note: z.string().nullable(),
})

export const zContact = z.object({
  id: zId,
  name: z.string(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  role: z.string().nullable(),
  isPrimary: z.boolean(),
})

export const zSupplierTerms = z.object({
  minOrderValueCents: z.int().nullable(),
  freightFreeFromCents: z.int().nullable(),
  leadTimeDays: z.int().nullable(),
  orderWeekdays: z.array(z.int()),
  deliveryWeekdays: z.array(z.int()),
  orderChannel: z.enum(ORDER_CHANNELS).nullable(),
  orderEmail: z.string().nullable(),
})

export const zPartnerListRow = z.object({
  id: zId,
  number: z.string(),
  name: z.string(),
  name2: z.string().nullable(),
  type: z.enum(PARTNER_TYPES),
  isCustomer: z.boolean(),
  isSupplier: z.boolean(),
  street: z.string().nullable(),
  zip: z.string().nullable(),
  city: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  paymentTermName: z.string().nullable(),
  priceGroupName: z.string().nullable(),
  priceMode: z.enum(['gross', 'net']).nullable(),
  blocked: z.boolean(),
  blockReason: z.string().nullable(),
  active: z.boolean(),
  updatedAt: zTimestamp,
})

export const zPartnerDetail = z.object({
  id: zId,
  version: z.int(),
  number: z.string(),
  isCustomer: z.boolean(),
  isSupplier: z.boolean(),
  type: z.enum(PARTNER_TYPES),
  name: z.string(),
  name2: z.string().nullable(),
  vatId: z.string().nullable(),
  taxNumber: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  gln: z.string().nullable(),
  paymentTerm: z.object({ id: zId, name: z.string() }).nullable(),
  priceGroup: z.object({ id: zId, name: z.string(), priceMode: z.enum(['gross', 'net']) }).nullable(),
  creditLimitCents: z.int().nullable(),
  discountBp: z.int(),
  minShelfLifeDays: z.int().nullable(),
  collectiveInvoicing: z.enum(COLLECTIVE_INVOICINGS),
  datevAccountCustomer: z.string().nullable(),
  datevAccountSupplier: z.string().nullable(),
  iban: z.string().nullable(),
  bic: z.string().nullable(),
  accountHolder: z.string().nullable(),
  einvoiceFormat: z.enum(EINVOICE_FORMATS).nullable(),
  /** null = Vorgabe des Betriebs (UBL) */
  einvoiceSyntax: z.enum(EINVOICE_SYNTAXES).nullable(),
  einvoiceAddress: z.string().nullable(),
  buyerReference: z.string().nullable(),
  paymentMethod: z.enum(PAYMENT_METHODS),
  sepaMandate: zSepaMandate.nullable(),
  requiresOrderRef: z.boolean(),
  supplierRefAtCustomer: z.string().nullable(),
  contractRef: z.string().nullable(),
  rebateNote: z.string().nullable(),
  /** Steuerfall (AP2c): Inland, EU-Unternehmen (K), Drittland (G), § 13b (AE) – bei Lieferanten immer Inland */
  taxCase: z.enum(TAX_CASES),
  /** Registernummer des Kunden (BT-47), z. B. „HRB 12345, Amtsgericht München“ */
  registerNumber: z.string().nullable(),
  blocked: z.boolean(),
  blockReason: z.string().nullable(),
  notes: z.string().nullable(),
  customFields: z.record(z.string(), z.unknown()),
  active: z.boolean(),
  anonymizedAt: zTimestamp.nullable(),
  addresses: z.array(zAddress),
  contacts: z.array(zContact),
  /** Lieferkonditionen – nur bei Lieferanten */
  terms: zSupplierTerms.nullable(),
  createdAt: zTimestamp,
  updatedAt: zTimestamp,
})

const zAccountSide = z.object({
  balanceCents: z.int(),
  overdueCents: z.int(),
  openCount: z.int(),
  oldestDueDate: z.string().nullable(),
})

export const zPartnerAccount = z.object({
  partnerId: zId,
  asOf: z.string(),
  /** Forderungen an den Partner (Kundenseite) */
  receivable: zAccountSide,
  /** Verbindlichkeiten gegenüber dem Partner (Lieferantenseite) */
  payable: zAccountSide,
  /** nicht zugeordnete Zahlungen: + = Guthaben des Partners bei uns */
  unallocatedCents: z.int(),
  creditLimitCents: z.int().nullable(),
  credit: z.object({
    status: z.enum(['ok', 'warning', 'blocked']),
    message: z.string(),
    exposureCents: z.int(),
    availableCents: z.int().nullable(),
  }),
  /** Leergutkonto: + = der Partner hält Leergut */
  deposits: z.array(
    z.object({
      depositTypeId: zId,
      name: z.string(),
      shortName: z.string().nullable(),
      account: z.enum(['customer', 'supplier']),
      qty: z.int(),
      amountCents: z.int(),
    }),
  ),
  depositTotalCents: z.int(),
})

/** Ergebnis von anlegen/ändern – mit Hinweisen, die das Speichern nicht verhindern (§5.2 Nr. 6 und 7). */
export const zPartnerSaved = zSaved.extend({ warnings: z.array(z.string()) })

// ---- Felder ---------------------------------------------------------------------------------------------------

/** Felder, die Kunden und Lieferanten teilen (ohne Standardwerte). */
const commonFields = {
  type: z.enum(PARTNER_TYPES),
  name: zText(200),
  name2: zOptionalText(200),
  vatId: zOptionalText(20),
  taxNumber: zOptionalText(30),
  email: zEmail,
  phone: zOptionalText(60),
  gln: zOptionalText(13),
  paymentTermId: zOptionalId,
  iban: zOptionalText(42),
  bic: zOptionalText(11),
  accountHolder: zOptionalText(160),
  notes: zOptionalText(4000),
  /** Zusatzfelder des Betriebs (platform.customField.list, entityType 'partner') */
  customFields: z.record(z.string(), z.unknown()).optional(),
  /** VOLLSTÄNDIGE Liste: vorhandene mit `id`, neue ohne; was fehlt, wird entfernt (sofern in keinem Beleg verwendet) */
  addresses: z.array(zAddressInput).max(50).optional(),
  contacts: z.array(zContactInput).max(50).optional(),
}

const customerFields = {
  priceGroupId: zOptionalId,
  /** Kreditlimit in Cent; null = kein Limit (Recht „Kreditlimit und Liefersperre setzen“) */
  creditLimitCents: zCentsAmount.nullish(),
  /** Kundenrabatt in Basispunkten */
  discountBp: zBasisPoints.optional(),
  /** überschreibt die Mindestrestlaufzeit der Artikel für diesen Kunden (Tage) */
  minShelfLifeDays: zDays.nullish(),
  collectiveInvoicing: z.enum(COLLECTIVE_INVOICINGS).optional(),
  datevAccountCustomer: zOptionalText(12),
  /**
   * E-Rechnung: null = nach gesetzlicher Regel (Pflicht für Geschäftskunden im Inland über 250 € ab dem Stichtag des
   * Betriebs); xrechnung/zugferd = immer (Wunsch des Kunden); pdf = nur, wo keine Pflicht besteht
   */
  einvoiceFormat: z.enum(EINVOICE_FORMATS).nullish(),
  /** Syntax der XRechnung: ubl | cii; null = Vorgabe des Betriebs */
  einvoiceSyntax: z.enum(EINVOICE_SYNTAXES).nullish(),
  /** Empfangsweg der E-Rechnung: E-Mail-Adresse oder `<Schema>:<Kennung>` (0204 Leitweg-ID, 9930 USt-IdNr., 0088 GLN, 0060 DUNS) */
  einvoiceAddress: zOptionalText(200),
  /** Käuferreferenz (BT-10); bei öffentlichen Stellen die Leitweg-ID mit Prüfziffer – nie Pflicht beim Speichern */
  buyerReference: zOptionalText(100),
  /** Zahlart: transfer (Überweisung, Standard), direct_debit (SEPA-Lastschrift, braucht das Mandat), cash */
  paymentMethod: z.enum(PAYMENT_METHODS).optional(),
  /** SEPA-Lastschriftmandat; null entfernt es */
  sepaMandate: zSepaMandateInput.nullish(),
  /** der Kunde verlangt seine Bestellnummer auf jeder Rechnung */
  requiresOrderRef: z.boolean().optional(),
  /** unsere Lieferantennummer beim Kunden (erscheint in der E-Rechnung) */
  supplierRefAtCustomer: zOptionalText(60),
  /** Rahmen- oder Bonusvertrag, z. B. „RV-2027-14“ */
  contractRef: zOptionalText(60),
  /** Hinweis auf vereinbarte Entgeltminderungen (Boni, Rückvergütungen) – steht auf jeder Rechnung */
  rebateNote: zOptionalText(1000),
  /**
   * Steuerfall: domestic (Inland, Standard) | eu_business (EU-Unternehmen mit USt-IdNr. → innergemeinschaftliche
   * Lieferung) | export (Drittland → Ausfuhr) | reverse_charge (§ 13b UStG). Neue Belegzeilen an den Kunden tragen
   * dann statt 19 %/7 % den Ersatzschlüssel des Betriebs; bestehende Belege bleiben. EU-Unternehmen brauchen die USt-IdNr.
   */
  taxCase: z.enum(TAX_CASES).optional(),
  /** Registernummer des Kunden (BT-47), z. B. „HRB 12345, Amtsgericht München“ – für E-Rechnungen nach § 13b ohne USt-IdNr. */
  registerNumber: zOptionalText(100),
  /** Liefersperre (Recht „Kreditlimit und Liefersperre setzen“) */
  blocked: z.boolean().optional(),
  blockReason: zOptionalText(300),
  /** der Kunde ist zugleich Lieferant (braucht zusätzlich das Schreibrecht für Lieferanten) */
  isSupplier: z.boolean().optional(),
}

const supplierFields = {
  datevAccountSupplier: zOptionalText(12),
  /** Lieferkonditionen als Ganzes */
  terms: zSupplierTermsInput.optional(),
  /** der Lieferant ist zugleich Kunde (braucht zusätzlich das Schreibrecht für Kunden) */
  isCustomer: z.boolean().optional(),
}

type RoleFields<R extends PartnerRole> = R extends 'customer' ? typeof customerFields : typeof supplierFields

const upsertCommon = {
  /** Kunden- bzw. Lieferantennummer – der Schlüssel des Abgleichs */
  number: zText(30),
  name: zText(200),
  name2: zOptionalText(200),
  type: z.enum(PARTNER_TYPES).optional(),
  vatId: zOptionalText(20),
  taxNumber: zOptionalText(30),
  email: zEmail,
  phone: zOptionalText(60),
  street: zOptionalText(160),
  zip: zOptionalText(12),
  city: zOptionalText(120),
  country: zCountry.optional(),
  /** Schlüssel der Zahlungsbedingung: 'net14', 'discount14_net30' … */
  paymentTerm: zOptionalText(60),
  iban: zOptionalText(42),
  active: z.boolean().optional(),
}

const TEXTS: Record<
  PartnerRole,
  { keywords: string[]; createHint: string; listHint: string; deactivate: string; accountHint: string }
> = {
  customer: {
    keywords: ['Kunde', 'Kunden', 'Debitor', 'Geschäftskunde', 'Privatkunde', 'Kundennummer'],
    createHint:
      'Die Kundennummer vergibt der Nummernkreis, wenn sie fehlt. Geschäftskunden bekommen Preisgruppe, ' +
      'Zahlungsbedingung und Lieferanschriften; Kreditlimit und Liefersperre brauchen ein eigenes Recht. ' +
      'Öffentliche Stellen tragen ihre Leitweg-ID als buyerReference (mit Prüfziffer); Lastschrift braucht das Mandat. ' +
      'Geschäftskunden im EU-Ausland mit USt-IdNr. bekommen taxCase eu_business, Kunden außerhalb der EU export, ' +
      'Leistungsempfänger nach § 13b reverse_charge – sonst rechnen ihre Belege mit deutscher Umsatzsteuer.',
    listHint: 'Filter: Art (privat, Unternehmen, öffentliche Stelle), Preisgruppe, gesperrt, aktiv.',
    deactivate: 'Der Kunde lässt sich nicht mehr für neue Belege wählen; offene Posten, Konten und Belege bleiben.',
    accountHint: 'offene Forderungen, Überfälliges, Ausschöpfung des Kreditlimits',
  },
  supplier: {
    keywords: ['Lieferant', 'Lieferanten', 'Kreditor', 'Großhändler', 'Lieferantennummer'],
    createHint:
      'Die Lieferantennummer vergibt der Nummernkreis, wenn sie fehlt. `terms` trägt Mindestbestellwert, Lieferzeit, ' +
      'Bestell- und Liefertage (1 = Montag … 7 = Sonntag) und den Bestellweg.',
    listHint: 'Filter: Art, aktiv.',
    deactivate: 'Der Lieferant lässt sich nicht mehr für neue Bestellungen wählen; Belege und Preishistorie bleiben.',
    accountHint: 'offene Verbindlichkeiten',
  },
}

// ---- Fabrik ---------------------------------------------------------------------------------------------------

function definePartnerContracts<const R extends PartnerRole>(role: R) {
  const one = PARTNER_ROLE_LABELS[role].one
  const many = PARTNER_ROLE_LABELS[role].many
  const text = TEXTS[role]
  const roleFields = (role === 'customer' ? customerFields : supplierFields) as RoleFields<R>
  const read: PermissionKey = `masterdata.${role}.read`
  const write: PermissionKey = `masterdata.${role}.write`
  const prefix = `masterdata.${role}` as const

  const list = defineContract({
    name: `${prefix}.list`,
    title: `${many} anzeigen`,
    description: `Listet ${many} mit Nummer, Anschrift, Zahlungsbedingung und Status.`,
    llm: {
      description:
        `Sucht und listet ${many}. \`q\` findet Nummer, Namensteile, Ort, E-Mail und USt-IdNr. ${text.listHint} ` +
        `Für alle Einzelheiten danach ${prefix}.get.`,
      keywords: [...text.keywords, 'suchen', 'Adresse'],
    },
    input: listInput({
      sortBy: ['name', 'number', 'city', 'updatedAt'],
      defaultSort: { by: 'name' },
      filters: {
        type: z.enum(PARTNER_TYPES).optional(),
        active: z.boolean().optional(),
        blocked: z.boolean().optional(),
        priceGroupId: zId.optional(),
      },
    }),
    output: listOutput(zPartnerListRow),
    permission: read,
    risk: 'read',
  })

  const get = defineContract({
    name: `${prefix}.get`,
    title: `${one} öffnen`,
    description: `Zeigt einen ${many === 'Kunden' ? 'Kunden' : 'Lieferanten'} mit Anschriften, Ansprechpartnern und Konditionen.`,
    llm: {
      description:
        `Liefert alle Stammdaten eines ${many === 'Kunden' ? 'Kunden' : 'Lieferanten'}: Anschriften (Rechnung, Lieferung), ` +
        'Ansprechpartner, Zahlungsbedingung, Steuer- und Bankangaben, E-Rechnung' +
        (role === 'customer' ? ', Preisgruppe, Kreditlimit, Sperre.' : ', Lieferkonditionen.') +
        ` Kontostand: ${prefix}.account.`,
      keywords: [...text.keywords, 'Anschrift', 'Ansprechpartner', 'Zahlungsziel'],
    },
    input: zById,
    output: zPartnerDetail,
    permission: read,
    risk: 'read',
  })

  const create = defineContract({
    name: `${prefix}.create`,
    title: `${one} anlegen`,
    description: `Legt einen ${many === 'Kunden' ? 'Kunden' : 'Lieferanten'} mit Anschriften und Ansprechpartnern an.`,
    llm: {
      description: `Legt einen neuen ${one === 'Kunde' ? 'Kunden' : 'Lieferanten'} an. Pflicht ist nur der Name. ${text.createHint}`,
      keywords: [...text.keywords, 'anlegen', 'neu', 'erfassen'],
    },
    input: z.strictObject({
      /** leer lassen: die Nummer kommt aus dem Nummernkreis */
      number: zOptionalText(30),
      ...commonFields,
      type: commonFields.type.default('business'),
      ...roleFields,
    }),
    output: zPartnerSaved.extend({ number: z.string() }),
    permission: write,
    risk: 'write',
    effects: { reversible: true },
  })

  const update = defineContract({
    name: `${prefix}.update`,
    title: `${one} ändern`,
    description: `Ändert Stammdaten, Anschriften, Ansprechpartner oder Konditionen. Nur die genannten Felder ändern sich.`,
    llm: {
      description:
        `Ändert einen ${one === 'Kunde' ? 'Kunden' : 'Lieferanten'} – nur die mitgegebenen Felder; null leert ein ` +
        'freiwilliges Feld. `addresses` und `contacts` sind jeweils die VOLLSTÄNDIGE Liste (vorhandene mit ihrer id); ' +
        'was fehlt, wird entfernt. Eine Anschrift, die in Belegen steht, lässt sich nicht entfernen.',
      keywords: [...text.keywords, 'ändern', 'Adresse ändern', 'umziehen', 'Zahlungsziel ändern'],
    },
    input: z.strictObject({
      id: zId,
      expectedVersion: zExpectedVersion,
      number: zOptionalText(30),
      ...commonFields,
      type: commonFields.type.optional(),
      name: commonFields.name.optional(),
      ...roleFields,
    }),
    output: zPartnerSaved,
    permission: write,
    risk: 'write',
    effects: { reversible: true, securitySensitive: true },
  })

  const deactivate = defineContract({
    name: `${prefix}.deactivate`,
    title: `${one} sperren`,
    description: text.deactivate,
    llm: {
      description:
        `Deaktiviert einen ${one === 'Kunde' ? 'Kunden' : 'Lieferanten'}. Gelöscht wird nichts. Die Vorschau nennt offene ` +
        `Posten. Rückgängig über ${prefix}.reactivate.`,
      keywords: [...text.keywords, 'deaktivieren', 'stilllegen', 'löschen', 'nicht mehr beliefern'],
    },
    input: zToggle,
    output: zSaved,
    permission: write,
    risk: 'destructive',
    effects: { reversible: true },
  })

  const reactivate = defineContract({
    name: `${prefix}.reactivate`,
    title: `${one} freigeben`,
    description: `Macht einen deaktivierten ${one === 'Kunde' ? 'Kunden' : 'Lieferanten'} wieder wählbar.`,
    llm: { description: `Gegenstück zu ${prefix}.deactivate.`, keywords: [...text.keywords, 'reaktivieren', 'freigeben'] },
    input: zToggle,
    output: zSaved,
    permission: write,
    risk: 'write',
    effects: { reversible: true },
  })

  const upsert = defineContract({
    name: `${prefix}.upsert`,
    title: `${many} abgleichen`,
    description: `Legt den ${one === 'Kunde' ? 'Kunden' : 'Lieferanten'} mit dieser Nummer an oder ändert ihn – für Importe und Schnittstellen.`,
    llm: {
      description:
        `Gleicht einen ${one === 'Kunde' ? 'Kunden' : 'Lieferanten'} über seine Nummer ab (anlegen oder ändern), mit einer ` +
        `Rechnungsanschrift. Für Importe; im Gespräch ${prefix}.create bzw. ${prefix}.update verwenden.`,
      keywords: ['Import', 'Abgleich'],
    },
    input: z.strictObject({
      ...upsertCommon,
      ...(role === 'customer'
        ? {
            /** Schlüssel der Preisgruppe: 'retail', 'business' … */
            priceGroup: zOptionalText(60),
            creditLimitCents: zCentsAmount.nullish(),
          }
        : {}),
    }),
    output: z.object({ id: zId, created: z.boolean() }),
    permission: write,
    risk: 'write',
  })

  const account = defineContract({
    name: `${prefix}.account`,
    title: `Konto eines ${one === 'Kunde' ? 'Kunden' : 'Lieferanten'} anzeigen`,
    description: `Kontostand aus den offenen Posten – ${text.accountHint}.`,
    llm: {
      description:
        `Liefert für einen ${one === 'Kunde' ? 'Kunden' : 'Lieferanten'} ${text.accountHint}. Beträge in Cent, ` +
        'Stückzahlen weiterer Konten in Tausendstel (+ = der Partner hält sie). Nur lesen.',
      keywords: ['offene Posten', 'Saldo', 'Kreditlimit', 'überfällig'],
    },
    input: zById,
    output: zPartnerAccount,
    permission: `masterdata.${role}.account`,
    risk: 'read',
  })

  return { role, list, get, create, update, deactivate, reactivate, upsert, account }
}

export const customerContracts = definePartnerContracts('customer')
export const supplierContracts = definePartnerContracts('supplier')

// Einzeln exportiert: scripts/gen-registry.ts findet Verträge nur als benannte Exporte von contract.ts.
export const customerList = customerContracts.list
export const customerGet = customerContracts.get
export const customerCreate = customerContracts.create
export const customerUpdate = customerContracts.update
export const customerDeactivate = customerContracts.deactivate
export const customerReactivate = customerContracts.reactivate
export const customerUpsert = customerContracts.upsert
export const customerAccount = customerContracts.account

export const supplierList = supplierContracts.list
export const supplierGet = supplierContracts.get
export const supplierCreate = supplierContracts.create
export const supplierUpdate = supplierContracts.update
export const supplierDeactivate = supplierContracts.deactivate
export const supplierReactivate = supplierContracts.reactivate
export const supplierUpsert = supplierContracts.upsert
export const supplierAccount = supplierContracts.account

/** Wohin eine Verknüpfung auf einen Partner führt, wenn nur seine ID bekannt ist (Belege, Suche, Verlauf). */
export const partnerLocate = defineContract({
  name: 'masterdata.partner.locate',
  title: 'Partner nachschlagen',
  description: 'Sagt zu einer Partner-ID, ob es ein Kunde, ein Lieferant oder beides ist.',
  llm: {
    description:
      'Liefert zu einer Partner-ID Nummer, Name und Rollen (Kunde, Lieferant). Verwenden, wenn ein Beleg nur die ' +
      'Partner-ID nennt; danach masterdata.customer.get oder masterdata.supplier.get.',
    keywords: ['Partner', 'Kunde oder Lieferant'],
  },
  input: zById,
  output: z.object({ id: zId, number: z.string(), name: z.string(), isCustomer: z.boolean(), isSupplier: z.boolean() }),
  permission: '@authenticated',
  risk: 'read',
})

/** Ein Kunde oder Lieferant wurde angelegt oder geändert. */
export const partnerChanged = defineEvent({
  name: 'masterdata.partner.changed',
  title: 'Kunde oder Lieferant geändert',
  payload: z.object({
    id: zId,
    number: z.string(),
    isCustomer: z.boolean(),
    isSupplier: z.boolean(),
    change: z.enum(['created', 'updated', 'deactivated', 'reactivated']),
  }),
})
